/**
 * Widget Registry - Central registry for all widget types
 * Allows dynamic registration and retrieval of widget components with enhanced metadata
 */

import { WIDGET_CATEGORIES, DEFAULT_FALLBACK_SIZES } from './config/constants';

class WidgetRegistry {
  constructor() {
    this.widgets = new Map();
  }

  /**
   * Register a widget type with enhanced metadata
   * @param {string} type - Widget type identifier (e.g., 'kpi', 'table')
   * @param {React.Component} component - Widget component
   * @param {Object} metadata - Widget metadata
   * @param {Object} metadata.defaultSizes - Default sizing constraints { minW, minH, defaultW, defaultH }
   * @param {string} metadata.category - Widget category (from WIDGET_CATEGORIES)
   * @param {string} metadata.displayName - Human-readable name for the widget
   * @param {string} metadata.description - Widget description
   * @param {string} metadata.icon - FontAwesome icon name
   * @param {boolean} metadata.supportsFilters - Whether widget responds to filters
   * @param {boolean} metadata.requiresDates - Whether widget requires date range
   * @param {Object} metadata.configSchema - JSON Schema for config validation
   */
  register(type, component, metadata = {}) {
    const {
      defaultSizes,
      category = WIDGET_CATEGORIES.CHART,
      displayName = type.charAt(0).toUpperCase() + type.slice(1),
      description = '',
      icon = 'chart-simple',
      supportsFilters = true,
      requiresDates = true,
      configSchema = null
    } = metadata;

    const mergedDefaultSizes = defaultSizes
      ? { ...DEFAULT_FALLBACK_SIZES, ...defaultSizes }
      : { ...DEFAULT_FALLBACK_SIZES };

    this.widgets.set(type, {
      component,
      defaultSizes: mergedDefaultSizes,
      category,
      displayName,
      description,
      icon,
      supportsFilters,
      requiresDates,
      configSchema
    });
  }

  /**
   * Get a widget component by type
   * @param {string} type - Widget type identifier
   * @returns {React.Component|null}
   */
  getComponent(type) {
    const widget = this.widgets.get(type);
    return widget?.component || null;
  }

  /**
   * Get full widget metadata by type
   * @param {string} type - Widget type identifier
   * @returns {Object|null}
   */
  getWidget(type) {
    return this.widgets.get(type) || null;
  }

  /**
   * Get default sizes for a widget type
   * @param {string} type - Widget type identifier
   * @returns {Object}
   */
  getDefaultSizes(type) {
    const widget = this.widgets.get(type);
    return widget?.defaultSizes || { ...DEFAULT_FALLBACK_SIZES };
  }

  /**
   * Get widget metadata (category, displayName, etc.)
   * @param {string} type - Widget type identifier
   * @returns {Object}
   */
  getMetadata(type) {
    const widget = this.widgets.get(type);
    if (!widget) return null;
    
    const { component, ...metadata } = widget;
    return metadata;
  }

  /**
   * Get all registered widget types
   * @returns {Array<string>}
   */
  getRegisteredTypes() {
    return Array.from(this.widgets.keys());
  }

  /**
   * Get widgets by category
   * @param {string} category - Widget category
   * @returns {Array<{type: string, metadata: Object}>}
   */
  getByCategory(category) {
    const results = [];
    for (const [type, widget] of this.widgets) {
      if (widget.category === category) {
        const { component, ...metadata } = widget;
        results.push({ type, metadata });
      }
    }
    return results;
  }

  /**
   * Get all widgets grouped by category
   * @returns {Object<string, Array>}
   */
  getAllByCategory() {
    const grouped = {};
    for (const [type, widget] of this.widgets) {
      const { category } = widget;
      if (!grouped[category]) {
        grouped[category] = [];
      }
      const { component, ...metadata } = widget;
      grouped[category].push({ type, metadata });
    }
    return grouped;
  }

  /**
   * Check if a widget type is registered
   * @param {string} type - Widget type identifier
   * @returns {boolean}
   */
  isRegistered(type) {
    return this.widgets.has(type);
  }

  /**
   * Check if a widget supports filters
   * @param {string} type - Widget type identifier
   * @returns {boolean}
   */
  supportsFilters(type) {
    const widget = this.widgets.get(type);
    return widget?.supportsFilters ?? true;
  }

  /**
   * Check if a widget requires date range
   * @param {string} type - Widget type identifier
   * @returns {boolean}
   */
  requiresDates(type) {
    const widget = this.widgets.get(type);
    return widget?.requiresDates ?? true;
  }

  /**
   * Clear all registrations (for debugging/testing)
   */
  clear() {
    this.widgets.clear();
  }
}

// Create singleton instance
const widgetRegistry = new WidgetRegistry();

export { WidgetRegistry };
export default widgetRegistry;
